<template>
  <div>
    <div class="card mb-0 shadow-sm border-0">
      <div class="card-header bg-dark text-white d-flex align-items-center justify-content-between py-2">
        <div>
          <font-awesome-icon icon="list-ol" />
          <strong v-if="!loading">&nbsp;{{ list.total }}&nbsp;</strong>
          <strong v-else>&nbsp;...&nbsp;</strong>
          <small>record(s) for {{ display_date }}</small>
        </div>
      </div>
      <div class="card-body">
        <b-row class="mb-3">
          <b-col sm="4" class="mb-2 mb-sm-0">
            <b-form-select v-model="selected_display_no" :options="options" size="sm" @change="changeDisplayNo"></b-form-select>
          </b-col>
          <b-col sm="4" class="mb-2 mb-sm-0">
            <b-form-input
              type="date"
              size="sm"
              autocomplete="off"
              v-model="date"
              @change.native="searchDate"
            />
          </b-col>
          <b-col sm="4">
            <b-input-group size="sm">
              <b-input-group-prepend>
                <b-input-group-text class="bg-dark text-white"><font-awesome-icon icon="search" /></b-input-group-text>
              </b-input-group-prepend>
              <b-form-input
                type="text"
                placeholder="Search ID or name"
                autocomplete="off"
                v-model="search_term"
                @keyup.native="search"
              />
              <b-input-group-append>
                <b-button variant="dark" size="sm" @click="clearSearch" :disabled="search_term==''">
                  <font-awesome-icon icon="times" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-col>
        </b-row>

        <b-table
          responsive
          hover
          striped
          bordered
          small
          :items="table_items"
          :fields="captions"
          :busy="loading"
          show-empty
          head-variant="dark"
        >
          <div class="text-center my-3" slot="table-busy">
            <b-spinner class="align-middle"></b-spinner>
            <div class="mt-2"><strong>Loading...</strong></div>
          </div>

          <template v-slot:cell(employee)="data">
            <div class="text-left">
              <div class="font-weight-bold text-uppercase">{{ data.item.manpower_details.full_name }}</div>
              <b-badge variant="info">ID : {{ data.item.manpower_details.id_number }}</b-badge>
            </div>
          </template>

          <template v-slot:cell(am_in)="data">
            <div class="text-center">
              <img v-if="data.item.am_in_image" :src="assets_path.OFFICE_LOGS_URL + data.item.am_in_image" class="timelog-thumb d-block mx-auto mb-1" />
              <span class="d-block">{{ timeText(data.item.formatted_am_in) }}</span>
            </div>
          </template>
          <template v-slot:cell(am_out)="data">
            <div class="text-center">
              <img v-if="data.item.am_out_image" :src="assets_path.OFFICE_LOGS_URL + data.item.am_out_image" class="timelog-thumb d-block mx-auto mb-1" />
              <span class="d-block">{{ timeText(data.item.formatted_am_out) }}</span>
            </div>
          </template>
          <template v-slot:cell(pm_in)="data">
            <div class="text-center">
              <img v-if="data.item.pm_in_image" :src="assets_path.OFFICE_LOGS_URL + data.item.pm_in_image" class="timelog-thumb d-block mx-auto mb-1" />
              <span class="d-block">{{ timeText(data.item.formatted_pm_in) }}</span>
            </div>
          </template>
          <template v-slot:cell(pm_out)="data">
            <div class="text-center">
              <img v-if="data.item.pm_out_image" :src="assets_path.OFFICE_LOGS_URL + data.item.pm_out_image" class="timelog-thumb d-block mx-auto mb-1" />
              <span class="d-block">{{ timeText(data.item.formatted_pm_out) }}</span>
            </div>
          </template>
          <template v-slot:cell(ot_in)="data">
            <div class="text-center">
              <img v-if="data.item.ot_in_image" :src="assets_path.OFFICE_LOGS_URL + data.item.ot_in_image" class="timelog-thumb d-block mx-auto mb-1" />
              <span class="d-block">{{ timeText(data.item.formatted_ot_in) }}</span>
            </div>
          </template>
          <template v-slot:cell(ot_out)="data">
            <div class="text-center">
              <img v-if="data.item.ot_out_image" :src="assets_path.OFFICE_LOGS_URL + data.item.ot_out_image" class="timelog-thumb d-block mx-auto mb-1" />
              <span class="d-block">{{ timeText(data.item.formatted_ot_out) }}</span>
            </div>
          </template>

        </b-table>

        <nav v-if="list.total>0">
          <b-pagination
            :disabled="loading"
            @input="getAll"
            align="right"
            :total-rows="total_rows"
            :per-page="per_page"
            v-model="list.current_page"
            class="mb-0"
          ></b-pagination>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
  import SwalMixin from '@/views/mixins/Swal.js'
  import { assets_path } from '@/config'
  import { kioskAxios, isKioskUnauthorized } from '@/kiosk'

  export default {
    name: 'office-timelog-table',
    mixins: [SwalMixin],
    props: {
      api_url: { type: String, required: true },
      // When true, requests use the kiosk device token instead of the user login.
      kiosk: { type: Boolean, default: false },
    },
    data() {
      return {
        assets_path: assets_path,
        list: { data: [], current_page: 1, total: 0, per_page: 1 },
        loading: true,
        search_term: '',
        searchTimeout: null,
        date: '',
        options: [
          { value: 10, text: 'Display 10 records' },
          { value: 25, text: 'Display 25 records' },
          { value: 50, text: 'Display 50 records' },
          { value: 100, text: 'Display 100 records' }
        ],
        selected_display_no: 10,
        fields: [
          { key: 'employee', label: 'Employee' },
          { key: 'am_in', label: 'AM IN' },
          { key: 'am_out', label: 'AM OUT' },
          { key: 'pm_in', label: 'PM IN' },
          { key: 'pm_out', label: 'PM OUT' },
          { key: 'ot_in', label: 'OT IN' },
          { key: 'ot_out', label: 'OT OUT' },
        ],
      }
    },
    computed: {
      table_items() { return this.list.data; },
      per_page() { return parseInt(this.list.per_page); },
      total_rows() { return parseInt(this.list.total); },
      captions() { return this.fields; },
      display_date() { return this.date || 'today'; },
    },
    mounted() {
      this.getAll();
    },
    methods: {
      timeText(formatted) {
        if (!formatted || formatted.default === '') return '--';
        return formatted.other_formats.format_1;
      },
      getAll() {
        this.loading = true;
        const http = this.kiosk ? kioskAxios : this.axios;
        http.get(this.api_url + '?page=' + this.list.current_page + '&per_page=' + this.selected_display_no + '&search=' + this.search_term + '&date=' + this.date)
          .then(response => {
            this.list = response.data;
            this.loading = false;
          }).catch(error => {
            console.log(error);
            this.loading = false;
            if (this.kiosk && isKioskUnauthorized(error)) this.$emit('unauthorized', error);
          });
      },
      search() {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.list.current_page = 1;
          this.getAll();
        }, 700);
      },
      searchDate() {
        this.list.current_page = 1;
        this.getAll();
      },
      clearSearch() {
        this.search_term = '';
        this.getAll();
      },
      changeDisplayNo() {
        this.list.current_page = 1;
        this.getAll();
      }
    }
  }
</script>

<style scoped>
  .timelog-thumb {
    width: 42px;
    height: 42px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #dee2e6;
  }
</style>
